import React, { useEffect, useMemo, useRef, useState } from 'react';
import md5 from 'md5';
import ReactQuill from 'react-quill';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import 'react-quill/dist/quill.snow.css';
import { zipSync } from 'fflate';
import ReadMailRightWidget from './readMailRightWidget';
import {
    v4_add_mailbox_recipients,
    v4_fetch_mailbox_recipients,
    v4_mailbox_attachment_content,
    v4_mailbox_attachments,
    v4_mailbox_email,
} from '../../actions/v4/mailBox';
import {
    buildAdhocRecipientOption,
    filterRecipientOption,
    mapRecipientOptions,
    mergeRecipientOptions,
    normalizeRecipientEmail,
} from './recipientSelectUtils';
import { formatLocalDateTime } from '../../dateTimeFormat';

const MailResponseIndicators = ({ mail }) => {
    const isReplied = Boolean(mail?.isReplied);
    const isForwarded = Boolean(mail?.isForwarded);

    if (!isReplied && !isForwarded) {
        return null;
    }

    return (
        <span className="mail-response-indicator" aria-label="Mail action status">
            {isReplied && (
                <i
                    className="mdi mdi-reply mail-response-icon-replied"
                    title="Replied"
                    aria-label="Replied"
                />
            )}
            {isForwarded && (
                <i
                    className="mdi mdi-forward mail-response-icon-forwarded"
                    title="Forwarded"
                    aria-label="Forwarded"
                />
            )}
        </span>
    );
};

const ReadMail = ({ data, closeMail, handleMailAction, selectedMailboxId, selectedMailboxEmail, folder, isDraftFolder }) => {
    const [replyMode, setReplyMode] = useState(false);
    const [replyAction, setReplyAction] = useState('reply');
    const [replyText, setReplyText] = useState('');
    const [replyRecipients, setReplyRecipients] = useState({ to: [], cc: [], bcc: [] });
    const [recipientOptions, setRecipientOptions] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [mailData, setMailData] = useState(data || {});
    const [mailLoading, setMailLoading] = useState(false);
    const [mailError, setMailError] = useState('');
    const [attachments, setAttachments] = useState(data?.attachments || []);
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [attachmentsError, setAttachmentsError] = useState('');
    const [attachmentBusyMap, setAttachmentBusyMap] = useState({});
    const [selectedAttachmentKeys, setSelectedAttachmentKeys] = useState([]);
    const attachmentContentCacheRef = useRef(new Map());
    const replyComposerRef = useRef(null);
    const replyEditorRef = useRef(null);

    const getAttachmentName = (attachment = {}, fallback = 'attachment') => (
        attachment?.filename
        || attachment?.name
        || attachment?.originalFileName
        || fallback
    );
    const getFileExtension = (filename = '') => {
        const normalized = String(filename || '').trim().toLowerCase();
        const dotIndex = normalized.lastIndexOf('.');
        if (dotIndex === -1 || dotIndex === normalized.length - 1) return '';
        return normalized.slice(dotIndex + 1);
    };
    const inferContentTypeFromFilename = (filename = '') => {
        const extension = getFileExtension(filename);
        const extensionMap = {
            png: 'image/png',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            gif: 'image/gif',
            webp: 'image/webp',
            bmp: 'image/bmp',
            svg: 'image/svg+xml',
            pdf: 'application/pdf',
            txt: 'text/plain',
            csv: 'text/csv',
            log: 'text/plain',
            md: 'text/markdown',
            json: 'application/json',
            xml: 'application/xml',
            html: 'text/html',
            htm: 'text/html',
            xls: 'application/vnd.ms-excel',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
            xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
            xlt: 'application/vnd.ms-excel',
            xltx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
            xltm: 'application/vnd.ms-excel.template.macroEnabled.12',
            xlam: 'application/vnd.ms-excel.addin.macroEnabled.12',
        };

        return extensionMap[extension] || '';
    };
    const resolveAttachmentContentType = (attachment = {}) => {
        const rawCandidates = [
            attachment?.contentType,
            attachment?.content_type,
            attachment?.mimeType,
            attachment?.mime_type,
        ];

        const normalizedCandidate = rawCandidates
            .map((item) => String(item || '').trim())
            .find((item) => item.length > 0);

        const inferredFromName = inferContentTypeFromFilename(getAttachmentName(attachment, ''));
        const isGenericBinary = String(normalizedCandidate || '').toLowerCase() === 'application/octet-stream';

        if (normalizedCandidate && !isGenericBinary) {
            return normalizedCandidate;
        }

        if (inferredFromName) {
            return inferredFromName;
        }

        return normalizedCandidate || 'application/octet-stream';
    };
    const isImage = (contentType = '') => String(contentType || '').toLowerCase().startsWith('image/');
    const isPdfContentType = (contentType = '') => String(contentType || '').toLowerCase().includes('pdf');
    const isPdfAttachment = (attachment = {}) => {
        const contentType = resolveAttachmentContentType(attachment);
        const filename = String(getAttachmentName(attachment, '') || '').toLowerCase();
        return isPdfContentType(contentType) || filename.endsWith('.pdf');
    };
    const isTextAttachment = (attachment = {}) => {
        const contentType = String(resolveAttachmentContentType(attachment) || '').toLowerCase();
        const filename = String(getAttachmentName(attachment, '') || '').toLowerCase();
        return contentType.startsWith('text/') || contentType.includes('json') || contentType.includes('xml') || /\.(txt|csv|log|json|xml|md)$/i.test(filename);
    };
    const isExcelAttachment = (attachment = {}) => {
        const contentType = String(resolveAttachmentContentType(attachment) || '').toLowerCase();
        const filename = String(getAttachmentName(attachment, '') || '').toLowerCase();
        return (
            /\.(xls|xlsx|xlsm|xlsb|xlt|xltx|xltm|xlam)$/i.test(filename)
            || contentType.includes('vnd.ms-excel')
            || contentType.includes('spreadsheetml')
        );
    };
    const getAttachmentKey = (attachment, index) => `${index}:${getAttachmentName(attachment, 'attachment')}`;
    const getAttachmentSource = (attachment = {}) => {
        if (!attachment?.content) return null;
        const contentType = resolveAttachmentContentType(attachment);
        return `data:${contentType};base64,${attachment.content}`;
    };
    const canPreviewAttachment = (attachment = {}) => !attachment?.isTooLarge && !isExcelAttachment(attachment);
    const getPreviewUnavailableReason = (attachment = {}) => {
        if (attachment?.isTooLarge) {
            return 'Too large to preview in app';
        }
        if (isExcelAttachment(attachment)) {
            return 'Preview is not supported for Excel files';
        }
        return '';
    };
    const isAttachmentBusy = (attachment, index) => Boolean(attachmentBusyMap[getAttachmentKey(attachment, index)]);
    const setAttachmentBusy = (attachment, index, isBusy) => {
        const key = getAttachmentKey(attachment, index);
        setAttachmentBusyMap((prev) => {
            if (isBusy) {
                return { ...prev, [key]: true };
            }

            if (!prev[key]) {
                return prev;
            }

            const next = { ...prev };
            delete next[key];
            return next;
        });
    };
    const setAttachmentContentForIndex = (index, contentPayload = {}) => {
        setAttachments((prev) => prev.map((item, itemIndex) => (
            itemIndex === index ? { ...item, ...contentPayload } : item
        )));
    };
    const cacheAttachmentContent = (attachmentKey, contentPayload) => {
        attachmentContentCacheRef.current.set(attachmentKey, contentPayload);
        if (attachmentContentCacheRef.current.size > 20) {
            const firstKey = attachmentContentCacheRef.current.keys().next().value;
            if (firstKey) {
                attachmentContentCacheRef.current.delete(firstKey);
            }
        }
    };
    const ensureAttachmentContent = async (attachment, index) => {
        if (!attachment?.id) {
            return null;
        }

        const attachmentKey = getAttachmentKey(attachment, index);
        const cached = attachmentContentCacheRef.current.get(attachmentKey);
        if (cached?.content) {
            if (!attachment?.content) {
                setAttachmentContentForIndex(index, cached);
            }
            return cached;
        }

        setAttachmentBusy(attachment, index, true);
        try {
            const res = await v4_mailbox_attachment_content(mailData.messageId, attachment.id, folder, selectedMailboxId);
            if (!res?.status) {
                throw new Error(res?.error || 'Unable to load attachment content');
            }

            const payload = res?.result?.attachment || {};
            if (!payload?.content) {
                throw new Error('Attachment content is unavailable');
            }

            cacheAttachmentContent(attachmentKey, payload);
            setAttachmentContentForIndex(index, payload);
            return payload;
        } finally {
            setAttachmentBusy(attachment, index, false);
        }
    };
    const triggerAttachmentDownload = async (attachment, index) => {
        try {
            const loadedAttachment = attachment?.content ? attachment : await ensureAttachmentContent(attachment, index);
            const src = getAttachmentSource(loadedAttachment);
            if (!src) return;

            const link = document.createElement('a');
            link.href = src;
            link.download = getAttachmentName(loadedAttachment, getAttachmentName(attachment, `attachment-${index + 1}`));
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            setAttachmentsError(error?.message || 'Unable to download this attachment.');
        }
    };
    const base64ToUint8Array = (base64Content = '') => {
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i += 1) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    };
    const sanitizeFilename = (value = '') => String(value || 'attachments')
        .replace(/[\\/:*?"<>|]+/g, '_')
        .trim();
    const decodeAttachmentText = (base64Content = '') => {
        try {
            const bytes = base64ToUint8Array(base64Content);
            return new TextDecoder('utf-8').decode(bytes);
        } catch (error) {
            return '';
        }
    };
    const escapeHtml = (value = '') => String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    const getGravatarUrl = (email = '', size = 80) => {
        const hash = md5(String(email || '').trim().toLowerCase());
        return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`;
    };

    useEffect(() => {
        setMailData(data || {});
        setMailLoading(false);
        setMailError('');
        setAttachments(data?.attachments || []);
        setAttachmentsError('');
        setAttachmentBusyMap({});
        setSelectedAttachmentKeys([]);
        setReplyMode(false);
        setReplyAction('reply');
        setReplyText('');
        setReplyRecipients({ to: [], cc: [], bcc: [] });
        attachmentContentCacheRef.current.clear();
    }, [data]);

    const needsMailFetch = useMemo(() => {
        return Boolean(mailData?.isSummary && mailData?.messageId && selectedMailboxId);
    }, [mailData?.isSummary, mailData?.messageId, selectedMailboxId]);

    useEffect(() => {
        if (!needsMailFetch) return;

        let cancelled = false;
        setMailLoading(true);
        setMailError('');

        v4_mailbox_email(mailData.messageId, folder, selectedMailboxId)
            .then((res) => {
                if (cancelled) return;

                if (res.status) {
                    const fetchedMail = res.result?.mail || {};
                    setMailData((prev) => ({ ...prev, ...fetchedMail, isSummary: false }));
                    if (Array.isArray(fetchedMail.attachments)) {
                        setAttachments(fetchedMail.attachments);
                    }
                } else {
                    setMailError(res.error || 'Failed to load email content');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setMailError('Failed to load email content');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setMailLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [needsMailFetch, mailData?.messageId, folder, selectedMailboxId]);

    useEffect(() => {
        if (!mailData?.hasAttachments || !selectedMailboxId || !mailData?.messageId) {
            if (!mailData?.hasAttachments) {
                setAttachments([]);
                attachmentContentCacheRef.current.clear();
            }
            return;
        }

        let cancelled = false;
        setAttachmentsLoading(true);
        setAttachmentsError('');
        v4_mailbox_attachments(mailData.messageId, folder, selectedMailboxId)
            .then((res) => {
                if (cancelled) return;
                if (res.status) {
                    const nextAttachments = Array.isArray(res.result?.attachments) ? res.result.attachments : [];
                    setAttachments(nextAttachments);
                    attachmentContentCacheRef.current.clear();
                } else {
                    setAttachmentsError(res.error || 'Failed to load attachments');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAttachmentsError('Failed to load attachments');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setAttachmentsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [mailData?.hasAttachments, mailData?.messageId, folder, selectedMailboxId]);

    useEffect(() => {
        setSelectedAttachmentKeys((prev) => {
            const allowedKeys = attachments
                .map((att, index) => getAttachmentKey(att, index));
            return prev.filter((key) => allowedKeys.includes(key));
        });
    }, [attachments]);

    const formatRecipientList = (value) => {
        if (Array.isArray(value)) {
            const normalized = value
                .map((entry) => {
                    if (typeof entry === 'string') {
                        return entry.trim();
                    }
                    if (entry && typeof entry === 'object') {
                        if (entry.address && entry.name) {
                            return `${entry.name} <${entry.address}>`;
                        }
                        return String(entry.address || entry.name || '').trim();
                    }
                    return '';
                })
                .filter(Boolean);

            return normalized.join(', ');
        }

        return String(value || '').trim();
    };
    const extractRecipientEmails = (value) => {
        const values = Array.isArray(value) ? value : [value];
        const resolved = values
            .flatMap((entry) => {
                if (!entry) return [];
                if (typeof entry === 'string') {
                    return entry
                        .split(/[;,]+/)
                        .map((item) => normalizeRecipientEmail(item))
                        .filter(Boolean);
                }
                if (typeof entry === 'object') {
                    const nested = entry?.address || entry?.email || entry?.emailAddress?.address || '';
                    return nested
                        ? String(nested)
                            .split(/[;,]+/)
                            .map((item) => normalizeRecipientEmail(item))
                            .filter(Boolean)
                        : [];
                }
                return [];
            })
            .filter(Boolean);

        return [...new Set(resolved)];
    };
    const toRecipientOptions = (emails = []) => (emails || [])
        .map((email) => buildAdhocRecipientOption(email))
        .filter(Boolean);
    const getDefaultReplyRecipients = (actionType = 'reply') => {
        if (actionType === 'forward') {
            return {
                to: [],
                cc: [],
                bcc: []
            };
        }
        if (actionType === 'draftSend') {
            return {
                to: extractRecipientEmails(mailData?.to),
                cc: extractRecipientEmails(mailData?.cc),
                bcc: extractRecipientEmails(mailData?.bcc)
            };
        }

        const ownAddress = normalizeRecipientEmail(selectedMailboxEmail);
        const fromCandidates = extractRecipientEmails([
            mailData?.replyTo,
            mailData?.fromAddress,
            mailData?.from
        ]).filter((email) => email !== ownAddress);

        const fallbackToCandidates = extractRecipientEmails(mailData?.to).filter((email) => email !== ownAddress);
        const fallbackCcCandidates = extractRecipientEmails(mailData?.cc).filter((email) => email !== ownAddress);

        if (actionType === 'replyAll') {
            const nextTo = fromCandidates.length
                ? fromCandidates
                : (fallbackToCandidates[0] ? [fallbackToCandidates[0]] : []);
            const nextCc = [...new Set([...fallbackToCandidates, ...fallbackCcCandidates])]
                .filter((email) => !nextTo.includes(email));
            return {
                to: nextTo,
                cc: nextCc,
                bcc: []
            };
        }

        return {
            to: fromCandidates[0] ? [fromCandidates[0]] : [],
            cc: [],
            bcc: []
        };
    };
    const fetchRecipientSuggestions = async () => {
        try {
            const res = await v4_fetch_mailbox_recipients();
            if (!res?.status) return;

            const options = mapRecipientOptions(res.result);
            setRecipientOptions((prev) => mergeRecipientOptions(prev, options));
        } catch (error) {
            console.error('Failed to fetch mailbox recipients', error);
        }
    };
    const handleReplyRecipientChange = (selectedOptions, fieldName) => {
        setReplyRecipients((prev) => ({
            ...prev,
            [fieldName]: selectedOptions || [],
        }));
    };
    const handleCreateReplyRecipient = async (inputValue, fieldName) => {
        const email = normalizeRecipientEmail(inputValue);
        if (!email) return;

        const newOption = buildAdhocRecipientOption(email);
        if (!newOption) return;

        const existsInSuggestions = (recipientOptions || []).some((opt) => opt.value === email);

        setReplyRecipients((prev) => {
            const currentField = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
            const alreadySelected = currentField.some((opt) => opt.value === email);

            return {
                ...prev,
                [fieldName]: alreadySelected ? currentField : [...currentField, newOption],
            };
        });

        setRecipientOptions((prev) => mergeRecipientOptions(prev, [newOption]));

        if (!existsInSuggestions) {
            try {
                await v4_add_mailbox_recipients({
                    email,
                    name: email.includes('@') ? email.split('@')[0] : email,
                });
            } catch (error) {
                toast.error('Failed to save new email for suggestions.');
            }
        }
    };

    const toDisplay = formatRecipientList(mailData?.to) || 'N/A';
    const ccDisplay = formatRecipientList(mailData?.cc);
    const bccDisplay = formatRecipientList(mailData?.bcc);
    const quotedDate = formatLocalDateTime(mailData?.date);
    const subjectDisplay = escapeHtml(mailData?.subject || '(no subject)');
    const quotedContent = `
        <br/><br/>
        <blockquote style="border-left: 2px solid #ccc; margin: 10px 0; padding-left: 10px; color: #555;">
            On ${quotedDate}, ${escapeHtml(mailData?.fromName || 'Unknown Sender')} &lt;${escapeHtml(mailData?.fromAddress || 'N/A')}&gt; wrote:<br/>
            <strong>To:</strong> ${escapeHtml(toDisplay)}<br/>
            ${ccDisplay ? `<strong>Cc:</strong> ${escapeHtml(ccDisplay)}<br/>` : ''}
            ${bccDisplay ? `<strong>Bcc:</strong> ${escapeHtml(bccDisplay)}<br/>` : ''}
            ${mailData?.html || mailData?.text || ''}
        </blockquote>
    `;
    const forwardedContent = `
        <br/><br/>
        <div style="margin: 10px 0; color: #555;">
            ---------- Forwarded message ----------<br/>
            <strong>From:</strong> ${escapeHtml(mailData?.fromName || 'Unknown Sender')} &lt;${escapeHtml(mailData?.fromAddress || 'N/A')}&gt;<br/>
            <strong>Date:</strong> ${escapeHtml(quotedDate || 'N/A')}<br/>
            <strong>Subject:</strong> ${subjectDisplay}<br/>
            <strong>To:</strong> ${escapeHtml(toDisplay)}<br/>
            ${ccDisplay ? `<strong>Cc:</strong> ${escapeHtml(ccDisplay)}<br/>` : ''}
            ${bccDisplay ? `<strong>Bcc:</strong> ${escapeHtml(bccDisplay)}<br/>` : ''}
        </div>
        <blockquote style="border-left: 2px solid #ccc; margin: 10px 0; padding-left: 10px; color: #555;">
            ${mailData?.html || mailData?.text || ''}
        </blockquote>
    `;

    const openReplyComposer = (actionType = 'reply') => {
        const defaults = getDefaultReplyRecipients(actionType);
        const defaultOptions = {
            to: toRecipientOptions(defaults.to),
            cc: toRecipientOptions(defaults.cc),
            bcc: toRecipientOptions(defaults.bcc),
        };

        setReplyAction(actionType);
        setReplyText(actionType === 'forward' ? forwardedContent : (actionType === 'draftSend' ? (mailData?.html || mailData?.text || '') : quotedContent));
        setReplyRecipients(defaultOptions);
        setRecipientOptions((prev) => mergeRecipientOptions(prev, [
            ...defaultOptions.to,
            ...defaultOptions.cc,
            ...defaultOptions.bcc,
        ]));
        fetchRecipientSuggestions();
        setReplyMode(true);
    };

    useEffect(() => {
        if (!replyMode) return;

        if (replyComposerRef.current) {
            replyComposerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        const focusTimer = setTimeout(() => {
            if (replyEditorRef.current?.focus) {
                replyEditorRef.current.focus();
            }
        }, 120);

        return () => clearTimeout(focusTimer);
    }, [replyMode, replyAction]);

    useEffect(() => {
        fetchRecipientSuggestions();
    }, []);

    useEffect(() => {
        const handleRecipientsChanged = () => fetchRecipientSuggestions();
        window.addEventListener('mailboxRecipientsChanged', handleRecipientsChanged);

        return () => window.removeEventListener('mailboxRecipientsChanged', handleRecipientsChanged);
    }, []);

    const mergedReplyRecipientOptions = useMemo(() => mergeRecipientOptions(
        recipientOptions,
        [...replyRecipients.to, ...replyRecipients.cc, ...replyRecipients.bcc]
    ), [recipientOptions, replyRecipients]);

    const sendReply = async () => {
        const toEmails = (replyRecipients.to || []).map((item) => item?.value).filter(Boolean);
        if (!toEmails.length) {
            toast.error('Please add at least one recipient in To.');
            return;
        }

        const ccEmails = (replyRecipients.cc || []).map((item) => item?.value).filter(Boolean);
        const bccEmails = (replyRecipients.bcc || []).map((item) => item?.value).filter(Boolean);
        const fullReply = replyText;

        const result = await handleMailAction(mailData.messageId, replyAction, fullReply, {
            to: toEmails,
            cc: ccEmails,
            bcc: bccEmails,
        });

        if (!result?.status) {
            return;
        }

        setReplyText('');
        setReplyAction('reply');
        setReplyRecipients({ to: [], cc: [], bcc: [] });
        setReplyMode(false);
    };
    const selectableAttachmentKeys = useMemo(() => (
        attachments
            .map((att, index) => getAttachmentKey(att, index))
    ), [attachments]);
    const allAttachmentsSelected = selectableAttachmentKeys.length > 0
        && selectableAttachmentKeys.every((key) => selectedAttachmentKeys.includes(key));
    const toggleAttachmentSelection = (attachmentKey) => {
        setSelectedAttachmentKeys((prev) => (
            prev.includes(attachmentKey)
                ? prev.filter((key) => key !== attachmentKey)
                : [...prev, attachmentKey]
        ));
    };
    const handleSelectAllAttachments = () => {
        setSelectedAttachmentKeys(allAttachmentsSelected ? [] : selectableAttachmentKeys);
    };
    const handleDownloadSelectedAttachments = async () => {
        try {
            const selectedKeySet = new Set(selectedAttachmentKeys);
            const selectedAttachmentMeta = attachments
                .map((att, index) => ({ attachment: att, index, key: getAttachmentKey(att, index) }))
                .filter((item) => selectedKeySet.has(item.key));

            const selectedAttachments = [];
            for (const item of selectedAttachmentMeta) {
                const loadedAttachment = item.attachment?.content
                    ? item.attachment
                    : await ensureAttachmentContent(item.attachment, item.index);

                if (loadedAttachment?.content) {
                    selectedAttachments.push({
                        ...item,
                        attachment: loadedAttachment,
                    });
                }
            }

            if (selectedAttachments.length === 0) return;

            if (selectedAttachments.length === 1) {
                const [item] = selectedAttachments;
                await triggerAttachmentDownload(item.attachment, item.index);
                return;
            }

            const zipFiles = {};
            const fileNameCount = {};

            selectedAttachments.forEach((item) => {
                const baseName = sanitizeFilename(getAttachmentName(item.attachment, `attachment-${item.index + 1}`));
                const duplicateCount = (fileNameCount[baseName] || 0) + 1;
                fileNameCount[baseName] = duplicateCount;
                const [nameWithoutExt, ...extParts] = baseName.split('.');
                const ext = extParts.length ? `.${extParts.join('.')}` : '';
                const finalName = duplicateCount > 1 ? `${nameWithoutExt} (${duplicateCount})${ext}` : baseName;
                zipFiles[finalName] = base64ToUint8Array(item.attachment.content);
            });

            const zipData = zipSync(zipFiles, { level: 6 });
            const zipBlob = new Blob([zipData], { type: 'application/zip' });
            const zipNameBase = sanitizeFilename(mailData?.subject || 'mail-attachments') || 'mail-attachments';
            const zipUrl = URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = zipUrl;
            link.download = `${zipNameBase}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(zipUrl), 1000);
        } catch (error) {
            setAttachmentsError(error?.message || 'Unable to download selected attachments.');
        }
    };
    const openAttachmentPreview = async (attachment, index) => {
        try {
            if (!canPreviewAttachment(attachment)) return;
            const loadedAttachment = attachment?.content ? attachment : await ensureAttachmentContent(attachment, index);
            if (!loadedAttachment?.content) return;
            const previewWindow = window.open('', '_blank');
            if (!previewWindow) return;

        const resolvedName = getAttachmentName(loadedAttachment, getAttachmentName(attachment, `Attachment ${index + 1}`));
        const safeName = escapeHtml(resolvedName);
        const contentType = resolveAttachmentContentType(loadedAttachment);
        const contentTypeLower = String(contentType || '').toLowerCase();
        const attachmentBlob = new Blob([base64ToUint8Array(loadedAttachment.content)], { type: contentType });
        const blobUrl = URL.createObjectURL(attachmentBlob);

        const baseStyles = `
            <style>
                body { margin: 0; font-family: Arial, sans-serif; background: #f5f7fb; }
                .preview-wrap { height: 100vh; display: flex; flex-direction: column; }
                .preview-head {
                    padding: 10px 14px;
                    border-bottom: 1px solid #d8deea;
                    background: #ffffff;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                }
                .preview-title { font-size: 14px; font-weight: 600; color: #23364a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .preview-actions a { text-decoration: none; color: #1453a6; font-size: 13px; }
                .preview-body { flex: 1; background: #ffffff; }
                .preview-frame, .preview-image { width: 100%; height: 100%; border: 0; }
                .preview-image { object-fit: contain; background: #ffffff; }
                .preview-text {
                    margin: 0;
                    padding: 16px;
                    white-space: pre-wrap;
                    word-break: break-word;
                    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                    font-size: 13px;
                    line-height: 1.45;
                    color: #1f2d3d;
                    overflow: auto;
                    height: calc(100vh - 54px);
                }
                .preview-empty {
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #4f5d6b;
                    font-size: 14px;
                    text-align: center;
                    padding: 20px;
                }
            </style>
        `;

        let previewBody = `<div class="preview-empty">Preview is not available for this file type. Use Download to open it locally.</div>`;
        if (isImage(contentTypeLower)) {
            previewBody = `<img class="preview-image" src="${blobUrl}" alt="${safeName}" />`;
        } else if (isTextAttachment(loadedAttachment)) {
            const textContent = escapeHtml(decodeAttachmentText(loadedAttachment.content) || 'Unable to preview this text attachment.');
            previewBody = `<pre class="preview-text">${textContent}</pre>`;
        } else if (isPdfAttachment(loadedAttachment)) {
            previewBody = `<iframe class="preview-frame" src="${blobUrl}" title="${safeName}"></iframe>`;
        } else if (contentTypeLower.startsWith('audio/')) {
            previewBody = `<div class="preview-empty"><audio controls src="${blobUrl}">Audio preview unavailable.</audio></div>`;
        } else if (contentTypeLower.startsWith('video/')) {
            previewBody = `<video class="preview-frame" controls src="${blobUrl}">Video preview unavailable.</video>`;
        }

            previewWindow.document.open();
            previewWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8" />
                <title>${safeName} - Preview</title>
                ${baseStyles}
            </head>
            <body>
                <div class="preview-wrap">
                    <div class="preview-head">
                        <div class="preview-title">${safeName}</div>
                        <div class="preview-actions">
                            <a href="${blobUrl}" download="${safeName}">Download</a>
                        </div>
                    </div>
                    <div class="preview-body">
                        ${previewBody}
                    </div>
                </div>
            </body>
            </html>
        `);
            previewWindow.document.close();

            previewWindow.addEventListener('beforeunload', () => {
                URL.revokeObjectURL(blobUrl);
            }, { once: true });
            setTimeout(() => URL.revokeObjectURL(blobUrl), 300000);
        } catch (error) {
            setAttachmentsError(error?.message || 'Unable to preview this attachment.');
        }
    };

    return (

        <div className='row'>
            <div className={`col-md-${expanded ? '12' : '7'}`}>
                <div className="card-body">
                    {/* Header */}
                    {/* Button group moved to the top */}
                    <div className="d-flex justify-content-start mb-2">
                        <div className="d-flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => closeMail()}
                                className="btn btn-secondary waves-effect me-2"
                            >
                                <i className="mdi mdi-arrow-left" /> Back
                            </button>

                            {/* Reply button */}
                            <button
                                type="button"
                                className="btn waves-light waves-effect"
                                style={{ backgroundColor: '#5d9cec', color: 'white' }}
                                onClick={() => {
                                    const nextAction = mailData.read === false ? 'read' : 'unread';
                                    handleMailAction(mailData.messageId, nextAction);
                                    setMailData((prev) => ({ ...prev, read: nextAction === 'read' }));
                                }}
                            >
                                <i className={`mdi ${mailData.read === false ? 'mdi-email-open-outline' : 'mdi-email-outline'}`} />{' '}
                                {mailData.read === false ? 'Mark as read' : 'Mark as unread'}
                            </button>

                            <button
                                type="button"
                                className="btn waves-light waves-effect"
                                style={{ backgroundColor: '#8cc152', color: 'white' }}
                                onClick={() => {
                                    openReplyComposer('reply');
                                }}
                            >
                                <i className="mdi mdi-reply" /> Reply
                            </button>

                            <button
                                type="button"
                                className="btn waves-light waves-effect"
                                style={{ backgroundColor: '#4fc1e9', color: 'white' }}
                                onClick={() => {
                                    openReplyComposer('replyAll');
                                }}
                            >
                                <i className="mdi mdi-reply-all" /> Reply all
                            </button>
                            <button
                                type="button"
                                className="btn waves-light waves-effect"
                                style={{ backgroundColor: '#f6bb42', color: 'white' }}
                                onClick={() => {
                                    openReplyComposer('forward');
                                }}
                            >
                                <i className="mdi mdi-forward" /> Forward
                            </button>
                            {isDraftFolder && (
                                <button
                                    type="button"
                                    className="btn waves-light waves-effect ms-1"
                                    style={{ backgroundColor: '#ff9800', color: 'white' }}
                                    onClick={() => {
                                        openReplyComposer('draftSend');
                                    }}
                                >
                                    <i className="mdi mdi-pencil" /> Edit Draft
                                </button>
                            )}

                            {/* Expand/Collapse toggle button */}
                            <button
                                type="button"
                                className="btn waves-light waves-effect"
                                style={{ backgroundColor: '#ac92ec', color: 'white' }}
                                onClick={() => setExpanded(prev => !prev)}
                            >
                                <i className="mdi mdi-arrow-expand-vertical" />
                                {expanded ? 'Collapse' : 'Expand'}
                            </button>
                        </div>
                    </div>

                    {replyMode && (
                        <div ref={replyComposerRef} className="card border mb-3" style={{ borderColor: '#d7e0ea', borderRadius: '10px' }}>
                            <div className="card-body">
                                <h5 className="mb-3">
                                    {replyAction === 'replyAll' ? 'Reply all' : (replyAction === 'forward' ? 'Forward' : (replyAction === 'draftSend' ? 'Edit & Send Draft' : 'Reply'))}
                                </h5>
                                <div className="mb-3">
                                    <label className="form-label mb-1">To</label>
                                    <CreatableSelect
                                        isMulti
                                        name="to"
                                        value={replyRecipients.to}
                                        options={mergedReplyRecipientOptions}
                                        filterOption={filterRecipientOption}
                                        onChange={(value) => handleReplyRecipientChange(value, 'to')}
                                        onCreateOption={(value) => handleCreateReplyRecipient(value, 'to')}
                                        classNamePrefix="select"
                                        placeholder="Add To recipients"
                                        isClearable
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label mb-1">CC</label>
                                    <CreatableSelect
                                        isMulti
                                        name="cc"
                                        value={replyRecipients.cc}
                                        options={mergedReplyRecipientOptions}
                                        filterOption={filterRecipientOption}
                                        onChange={(value) => handleReplyRecipientChange(value, 'cc')}
                                        onCreateOption={(value) => handleCreateReplyRecipient(value, 'cc')}
                                        classNamePrefix="select"
                                        placeholder="Add CC recipients"
                                        isClearable
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label mb-1">Bcc</label>
                                    <CreatableSelect
                                        isMulti
                                        name="bcc"
                                        value={replyRecipients.bcc}
                                        options={mergedReplyRecipientOptions}
                                        filterOption={filterRecipientOption}
                                        onChange={(value) => handleReplyRecipientChange(value, 'bcc')}
                                        onCreateOption={(value) => handleCreateReplyRecipient(value, 'bcc')}
                                        classNamePrefix="select"
                                        placeholder="Add Bcc recipients"
                                        isClearable
                                    />
                                </div>
                                <ReactQuill
                                    ref={replyEditorRef}
                                    theme="snow"
                                    value={replyText}
                                    onChange={setReplyText}
                                    placeholder={replyAction === 'forward' ? 'Write your forward message here...' : 'Write your reply here...'}
                                    style={{ height: '200px', marginBottom: '70px' }}
                                />
                                <div className="mt-4 d-flex gap-2 flex-wrap">
                                    <button
                                        className="btn"
                                        style={{
                                            backgroundImage: 'linear-gradient(90deg, rgb(105, 150, 181), rgb(43, 90, 126))',
                                            borderColor: 'rgb(43, 90, 126)',
                                            color: 'white'
                                        }}
                                        onClick={sendReply}
                                    >
                                        {replyAction === 'replyAll' ? 'Send Reply All' : (replyAction === 'forward' ? 'Send Forward' : (replyAction === 'draftSend' ? 'Send Draft' : 'Send Reply'))}
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setReplyMode(false);
                                            setReplyAction('reply');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Sender and message details */}
                    <div className="d-flex mb-4">
                        <div className="flex-shrink-0 me-3">
                            <img
                                className="rounded-circle avatar-sm"
                                src={getGravatarUrl(mailData.fromAddress)}
                                alt={mailData.fromName || 'Sender'}
                                onError={(e) => { e.target.src = 'assets/images/users/avatar-2.jpg'; }}
                            />
                        </div>
                        <div className="flex-grow-1">
                            <div className="mb-1">
                                <strong>From:</strong>{' '}
                                <span className="font-size-14">{mailData.fromName || 'Unknown Sender'}</span>{' '}
                                <small className="text-muted">&lt;{mailData.fromAddress || 'N/A'}&gt;</small>
                            </div>
                            <div>
                                <strong>To:</strong>{' '}
                                <span className="text-muted">{toDisplay}</span>
                            </div>
                            {ccDisplay && (
                                <div>
                                    <strong>Cc:</strong>{' '}
                                    <span className="text-muted">{ccDisplay}</span>
                                </div>
                            )}
                            {bccDisplay && (
                                <div>
                                    <strong>Bcc:</strong>{' '}
                                    <span className="text-muted">{bccDisplay}</span>
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Subject & Body */}
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                        <h4 className="font-size-16 mb-0">{mailData.subject || '(no subject)'}</h4>
                        <MailResponseIndicators mail={mailData} />
                    </div>
                    <div className="mail-content mb-3" style={{ height: '500px' }}>
                        {mailLoading && (
                            <div className="text-muted">Loading email content...</div>
                        )}

                        {!mailLoading && mailData.html ? (
                            <iframe
                                className='iframe-body'
                                title="email-body"
                                srcDoc={`
                            <html>
                                <head>
                                    <style>
                                        body { background-color: white !important; margin: 0; padding: 0; }
                                    </style>
                                </head>
                                <body>${mailData.html}</body>
                            </html>
                        `}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                sandbox="allow-same-origin"
                            />
                        ) : null}

                        {!mailLoading && !mailData.html && (
                            <p>{mailData.text || 'No content available.'}</p>
                        )}

                        {!mailLoading && mailError && (
                            <div className="text-danger">{mailError}</div>
                        )}
                    </div>

                    <hr />

                    {/* Attachments */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <h5 className="mb-2 mb-md-0">Attachments</h5>
                        <div className="btn-group">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={handleSelectAllAttachments}
                                disabled={selectableAttachmentKeys.length === 0}
                            >
                                {allAttachmentsSelected ? 'Clear selection' : 'Select all'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-success"
                                onClick={handleDownloadSelectedAttachments}
                                disabled={selectedAttachmentKeys.length === 0}
                            >
                                Download selected ({selectedAttachmentKeys.length})
                            </button>
                        </div>
                    </div>
                    <div className="row">
                        {attachmentsLoading && (
                            <div className="col-12 text-muted">Loading attachments...</div>
                        )}
                        {!attachmentsLoading && attachmentsError && (
                            <div className="col-12 text-danger">{attachmentsError}</div>
                        )}
                        {!attachmentsLoading && !attachmentsError && attachments?.length === 0 && mailData?.hasAttachments && (
                            <div className="col-12 text-muted">Attachments are available but could not be loaded.</div>
                        )}
                        {!attachmentsLoading && attachments?.map((att, index) => {
                            const attachmentKey = getAttachmentKey(att, index);
                            const displayName = getAttachmentName(att, `Attachment ${index + 1}`);
                            const isSelected = selectedAttachmentKeys.includes(attachmentKey);
                            const canPreview = canPreviewAttachment(att);
                            const previewUnavailableReason = getPreviewUnavailableReason(att);
                            const isBusy = isAttachmentBusy(att, index);
                            return (
                                <div className="col-xl-2 col-6 mb-3" key={index}>
                                    <div className="card border attachment-border">
                                        <div className="d-flex justify-content-end px-2 pt-2">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={isSelected}
                                                onChange={() => toggleAttachmentSelection(attachmentKey)}
                                            />
                                        </div>
                                        <div className="p-3 text-center">
                                            <i className="mdi mdi-file-document-outline fs-2"></i>
                                            <div className="mt-2">{displayName}</div>
                                        </div>
                                        <div className="py-2 text-center px-2">
                                            <div className="small text-muted mb-2 text-truncate" title={displayName}>
                                                {displayName}
                                            </div>
                                            <div className="small text-muted mb-2">
                                                {Number(att?.size) > 0 ? `${Math.ceil(Number(att.size) / 1024)} KB` : 'Unknown size'}
                                            </div>
                                            {att?.isTooLarge && (
                                                <div className="small text-danger mb-2">
                                                    Too large to preview in app
                                                </div>
                                            )}
                                            {!att?.isTooLarge && previewUnavailableReason && (
                                                <div className="small text-muted mb-2">
                                                    {previewUnavailableReason}
                                                </div>
                                            )}
                                            <div className="d-flex gap-2 justify-content-center flex-wrap">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-info"
                                                    onClick={() => openAttachmentPreview(att, index)}
                                                    disabled={!canPreview || isBusy}
                                                    title={previewUnavailableReason || 'Preview'}
                                                >
                                                    {isBusy ? 'Loading...' : 'Preview'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => triggerAttachmentDownload(att, index)}
                                                    disabled={isBusy}
                                                >
                                                    Download
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {!replyMode && !isDraftFolder && (
                        <div className="mt-4 d-flex gap-2 flex-wrap">
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    openReplyComposer('reply');
                                }}
                            >
                                <i className="mdi mdi-reply" /> Reply
                            </button>
                            <button
                                className="btn btn-info"
                                onClick={() => {
                                    openReplyComposer('replyAll');
                                }}
                            >
                                <i className="mdi mdi-reply-all" /> Reply all
                            </button>
                            <button
                                className="btn btn-warning"
                                onClick={() => {
                                    openReplyComposer('forward');
                                }}
                            >
                                <i className="mdi mdi-forward" /> Forward
                            </button>
                        </div>
                    )}
                    {!replyMode && isDraftFolder && (
                        <div className="mt-4 d-flex gap-2 flex-wrap">
                            <button
                                className="btn text-white"
                                style={{ backgroundColor: '#ff9800' }}
                                onClick={() => {
                                    openReplyComposer('draftSend');
                                }}
                            >
                                <i className="mdi mdi-pencil" /> Edit &amp; Send Draft
                            </button>
                        </div>
                    )}

                </div>
            </div>
            {/* Vertical divider (only when not expanded) */}
            {!expanded && (
                <div className="col-md-1 d-flex justify-content-center"
                    style={{ right: '40px',
                        marginRight: '-90px'
                    }}
                >
                    <div style={{ width: '4px', backgroundColor: '#ccc', height: '100%' }}></div>
                </div>
            )}


            {!expanded && <ReadMailRightWidget
                messageId={mailData.messageId}
                emailMetadata={{
                    subject: mailData?.subject || '',
                    fromName: mailData?.fromName || '',
                    fromAddress: mailData?.fromAddress || '',
                    to: Array.isArray(mailData?.to) ? mailData.to : (mailData?.to ? [mailData.to] : []),
                    cc: Array.isArray(mailData?.cc) ? mailData.cc : (mailData?.cc ? [mailData.cc] : []),
                    bcc: Array.isArray(mailData?.bcc) ? mailData.bcc : (mailData?.bcc ? [mailData.bcc] : []),
                }}

            />}


        </div>

    );
};

export default ReadMail;
